import { useTranslation, useIsRTL } from '@ufc/core'
import { Download, Plus, Button, cn } from '@ufc/uikit'

interface SalesOrdersHeaderProps {
  onCreateNew: () => void
  onExport: () => void
  createDisabled?: boolean
  createDisabledReason?: string
}

/**
 * Sales Orders Header - RTL Support via Document Direction
 *
 * RTL is handled by document.documentElement.dir set by I18nProvider.
 * Uses CSS logical properties (ms-, me-, start-, end-) that automatically
 * flip based on document direction. No manual flex-row-reverse needed.
 */
export function SalesOrdersHeader({
  onCreateNew,
  onExport,
  createDisabled = false,
  createDisabledReason,
}: SalesOrdersHeaderProps) {
  const { t } = useTranslation()
  const isRTL = useIsRTL()

  return (
    <div
      className="flex items-center justify-between"
      data-testid="sales-orders-header"
    >
      {/* Title section - text alignment handled by text-start (logical property) */}
      <div className="text-start">
        <h2 className="text-2xl font-bold text-foreground tracking-tight">{t('salesOrders.title')}</h2>
        <p className="text-muted-foreground mt-1">{t('salesOrders.subtitle')}</p>
      </div>
      {/* Action buttons - gap is direction-agnostic */}
      <div className="flex flex-col items-end gap-2">
        <div className="flex items-center gap-3">
        <Button variant="outline" className="rounded-xl" data-testid="sales-orders-export" onClick={onExport}>
          <Download className="w-4 h-4 me-2" />
          {t('common.export')}
        </Button>
        <Button
          onClick={onCreateNew}
          disabled={createDisabled}
          title={createDisabled ? createDisabledReason : undefined}
          className={cn(
            'text-primary-foreground hover:opacity-90 shadow-lg shadow-primary/25 rounded-xl px-6',
            isRTL ? 'bg-gradient-to-l from-primary to-primary/90' : 'bg-gradient-to-r from-primary to-primary/90'
          )}
          data-testid="sales-orders-create"
        >
          <Plus className="w-4 h-4 me-2" />
          {t('salesOrders.createSalesOrder')}
        </Button>
      </div>
        {createDisabled && createDisabledReason ? (
          <p className="text-xs text-muted-foreground">{createDisabledReason}</p>
        ) : null}
      </div>
    </div>
  )
}
